import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    GET_CASE_DATA, GET_MULTI_REFERRAL_CASE_DATA,
} from '../queries';
import { HighLevelDetails } from '../components/HighLevelDetails';
import { FirstAvailableDate } from '../components/FirstAvailableDate';
import { CaseManagementActionContainer, Spinner } from 'components/Shared';
import { useAuth } from 'components/Auth';
import { useIsActivityAccepted } from 'hooks/useIsActivityAccepted'
import { type Moment } from 'moment';
import { ACTIVE_STATUS_CODE } from 'constants/activityStatus';
import { DeclineCaseDialog } from '../../components/DeclineCaseDialog';
import { confirm } from 'components/Shared/ConfirmDialog';
import { AcceptanceDecision } from '../components/AcceptanceDecision';
import { PageMetadata } from 'components/Shared/PageMetadata';
import { isValueDefined } from 'utils';
import { useHistoryState } from 'hooks/useHistoryState';
import { ConfirmAcceptanceViewStages, useViewRouter, useConfirmAcceptanceMutations } from '../hooks';

import './ConfirmCaseAcceptance.scss';


interface ConfirmAcceptanceViewProps { }

export const ConfirmAcceptanceView: React.FC<ConfirmAcceptanceViewProps> = (_) => {
    const { t, ready: translationsReady } = useTranslation('caseManagement', { useSuspense: false });

    const { activityId: activityIdString } = useParams<{activityId: string}>();
    const [historyState] = useHistoryState<{ isMultiReferral: boolean }>();
    const { isMultiReferral = false } = historyState ?? {};
    const location = useLocation();
    const navigate = useNavigate();
    
    const { effectiveProviderData } = useAuth();
    const providerId = effectiveProviderData?.providerId ?? -1;

    const activityId = parseInt(activityIdString || '');

    const todoWorkflowId = location?.state?.workflowId;

    const { currentViewToShow, goToAcceptanceDecision, goToHighLevelDetails, goToFirstAvailableDate, handleBackClick } = useViewRouter();

    const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

    const [isConfirmed, setIsConfirmed] = useState(false);

    const caseDataQuery = isMultiReferral ? GET_MULTI_REFERRAL_CASE_DATA : GET_CASE_DATA

    const { data: caseDetailsData, loading: caseDataLoading } = useQuery(caseDataQuery, {
        variables: { activityId, providerId },
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only'
    });

    const isReferralExpired = new Date(caseDetailsData?.referral?.referralExpirationDate || '') <= new Date();

    useEffect(() => {
        if (!caseDataLoading && (!!caseDetailsData?.activity?.providerFirstAvailableAppointmentDate || isReferralExpired)) {
            goToAcceptanceDecision()
        }
    }, [caseDataLoading, caseDetailsData]);

    const onAcceptCompleted = () => {
        setIsConfirmed(true);
        goToHighLevelDetails();
    };

    const onDeclineCompleted = () => navigate('/', { replace: true });

    const {
        acceptReferral,
        acceptReferralMutation,
        handleDecline,
        declineCaseMutation,
        terminateToDo,
        declineCaseAfterDateOutsideParams,
        saveDateLoading,
        saveDateMutation,
    } = useConfirmAcceptanceMutations({
        activityId: activityId!,
        providerId: providerId!,
        isMultiReferral,
        onAcceptCompleted,
        onDeclineCompleted
    })

    const { data: isActivityAcceptedResponse, loading: isActivityAcceptedLoading } = useIsActivityAccepted(activityId, providerId);

    const showDateOutsideParamsConfirmDialog = () => {
        return confirm({
            message: t('date_outside_decline', {
                firstName: caseDetailsData?.activity?.client?.firstName,
                lastName: caseDetailsData?.activity?.client?.lastName
            }),
            title: t('decline_case'),
            cancelLabel: t('cancel'),
            confirmLabel: t('continue')
        });
    }

    const onSaveDateOutsideParams = async (date: Moment) => {
        const answer = await showDateOutsideParamsConfirmDialog()
        if (answer) {
            saveDateMutation(date).then(() => {
                return declineCaseAfterDateOutsideParams()
            });
        }
    }

    const onSaveDate = (date: Moment) => {
        saveDateMutation(date).then(() => {
            goToAcceptanceDecision()
        });
    }

    const onHighLevelDetailsContinue = () => {
        if (isConfirmed) {
            console.info(`🌎🌐🌍 Navigating to /clients/${activityId}`);
            return navigate(`/clients/${activityId}`);
        }
        goToFirstAvailableDate();
    }

    const renderHighLevelDetails = () => (
        <HighLevelDetails 
            onContinue={onHighLevelDetailsContinue} 
            activity={caseDetailsData?.activity}
            referral={caseDetailsData?.referral}
            confirmed={isConfirmed}
        />
    )

    const renderFirstAvailableDateView = () => (
        <FirstAvailableDate 
            activity={caseDetailsData?.activity} 
            onSaveDate={onSaveDate}
            onSaveDateOutsideParams={onSaveDateOutsideParams}
            loading={saveDateLoading || caseDataLoading || declineCaseMutation.loading}
            slaDate={caseDetailsData?.referral?.sLADate} 
        />
    )

    const renderAcceptanceDecisionView = () => (
        <AcceptanceDecision 
            onAccept={acceptReferral} 
            onDecline={() => setDeclineDialogOpen(true)}
            activity={caseDetailsData?.activity} 
            isExpired={isReferralExpired}
            loading={declineDialogOpen || declineCaseMutation.loading || acceptReferralMutation.loading}
        />
    )

    const getCurrentStep = () => {
        switch(currentViewToShow) {
            case ConfirmAcceptanceViewStages.ACCEPTANCE_DECISION_VIEW:
                return renderAcceptanceDecisionView()
            case ConfirmAcceptanceViewStages.HIGH_LEVEL_DETAILS_VIEW:
                return renderHighLevelDetails()
            case ConfirmAcceptanceViewStages.FIRST_AVAILABLE_DATE_VIEW:
                return renderFirstAvailableDateView()
            default:
                return renderHighLevelDetails()
        }
    } 


    const isEssentialDataLoading = caseDataLoading || isActivityAcceptedLoading;

    useEffect(() => {
        if (!isEssentialDataLoading) {
            // if Todo item through which the user was routed here is invalid
            if (isValueDefined(caseDetailsData?.activity?.activityStatusCode) && todoWorkflowId) {
                if (caseDetailsData?.activity?.activityStatusCode !== ACTIVE_STATUS_CODE || isActivityAcceptedResponse?.isActivityAcceptedByProvider) {
                    terminateToDo({ variables: { workflowId: todoWorkflowId }})
                }
            }
        }
    }, [todoWorkflowId, isActivityAcceptedResponse?.isActivityAcceptedByProvider, isEssentialDataLoading, caseDetailsData])

    const alreadyAcceptedByOtherProvider = acceptReferralMutation.error?.message.includes('MULTIREFERRAL_ACTIVITY_ASSIGNED_TO_ANOTHER_PROVIDER');

    return (
        <CaseManagementActionContainer onBackClick={isConfirmed ? undefined : handleBackClick} activityId={false} className="confirm_acceptance_container">
            {
                translationsReady && 
                <PageMetadata 
                    pageDescription={t('confirm_acceptance_page_description')} 
                    pageTitle={t('confirm_acceptance_page_title')}
                /> 
            }
            { 
                isEssentialDataLoading || !translationsReady
                ? <Spinner/>
                : <>
                    <h1 style={isConfirmed ? { textAlign: "center" } : {}}>
                        {isConfirmed ? t('confirmed') : t('confirm_acceptance') }
                    </h1>
                    {
                        (
                            isActivityAcceptedResponse?.isActivityAcceptedByProvider
                            || alreadyAcceptedByOtherProvider
                        )
                        ? <p className="not-accepted-yet">
                            {alreadyAcceptedByOtherProvider ? t('multireferral_activity_assigned_to_another_provider') : t('case_already_accepted')}
                        </p>
                        : getCurrentStep()
                    }
                </>
            }
            <DeclineCaseDialog open={declineDialogOpen}
                onDeclineCase={handleDecline}
                selectReason
                onCancel={() => setDeclineDialogOpen(false)}
                firstName={caseDetailsData?.activity?.client?.firstName ?? ''}
                lastName={caseDetailsData?.activity?.client?.lastName ?? ''}
            />
        </CaseManagementActionContainer>
    );
}

